/**
 * What the settings a client missed would cost, and why.
 *
 * **Read-only, and it must stay that way.** The endpoint writes no payment and creates no charge,
 * and nothing here should ever grow a POST. Charging a client is a decision a person takes with the
 * case in front of them; this exists so that person has the number and the reasoning together
 * rather than a figure nobody can explain.
 *
 * The server decides everything. Court level comes from the bonds rather than from a court name,
 * whether the newer terms apply comes from the client's agreement version, and both arrive with a
 * plain-English `reason` written to be shown on screen. The panel renders those sentences; it does
 * not compose its own, because two explanations of the same money would eventually disagree.
 */

export const COURT_LEVEL_LABELS: Record<string, string> = {
  JP_OR_MUNICIPAL: 'Justice or municipal court',
  COURT_OF_RECORD: 'County, county court at law or district court',
};

export interface FeeLine {
  label: string;
  amountCents: number;
}

export interface FeeAssessment {
  /** May this be charged at all? False is a complete answer — read `reason`. */
  chargeable: boolean;
  /** Zero whenever `chargeable` is false. Never a figure nobody may use. */
  amountCents: number;
  breakdown: FeeLine[];
  reason: string;
  /** Things a person must check that the data cannot answer. Never empty on a chargeable result. */
  caveats: string[];
}

export interface CourtLevelExplanation {
  level: string;
  reason: string;
  /** A bond exists but has not been filed — a state somebody may want to chase. */
  hasUnfiledAppealBond: boolean;
}

export interface AgreementStatus {
  version: string;
  newTermsApply: boolean;
  awaitingSignature: boolean;
  reason: string;
}

export interface MissedSetting {
  CourtEvent_guid: string;
  CourtEvent_Start: string;
  CourtEvent_Desc: string | null;
  note: string | null;
  assessment: FeeAssessment;
  /**
   * Returned rather than applied. Nothing on a calendar row reliably says "jury trial", so reading
   * it out of the description would be guesswork — and guessing on money is how a client gets
   * billed $1,500 for a pre-trial conference. A person adds this if it applies.
   */
  juryTrialWouldAdd: number;
}

export interface FailureToAppearFeeResponse {
  courtLevel: CourtLevelExplanation;
  agreement: AgreementStatus;
  /** One row per setting the client actually missed. Empty is the normal, good case. */
  missedSettings: MissedSetting[];
  schedule: Array<{ setting: string; court: string; amount: string }>;
}

export function formatFeeCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function apiHost(): string {
  return process.env.API_HOST?.trim() || window.location.origin;
}

export default class FailureToAppearFeeApi {
  static async get(ticketGuid: string): Promise<FailureToAppearFeeResponse> {
    const response = await fetch(
      `${apiHost()}/api/v1/manager/ticket/${ticketGuid}/failure-to-appear-fee`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      },
    );
    if (!response.ok) {
      throw new Error('Could not load the failure-to-appear assessment for this case.');
    }
    return response.json();
  }
}
